import React from 'react';
import {QuestionReport, QuestionReportAnswer, QuestionAnswerType} from '../../../../../incase-domain/analytics';
import ReportAnswerPercentage from '../../../../../incase-domain/components/report/answer/ReportAnswerPercentage';
import {getColor} from '../../../../../util/hexColor';
import {IQuestionElements} from '../../../../../incase-domain/elements/question';
import {getAnswerStatus} from './utils';

const PERCENTAGE_VISIBILITY_LIMIT = 5;

type RankDistributionDatum = {
    answerText: string;
    rankingScore?: number;
    averageRanking?: number;
    noOfAnswers?: number;
    [key: string]: string | number | undefined;
};

interface SequenceQuestionReportProps {
    questionReport: QuestionReport;
    question: IQuestionElements;
}

function SequenceQuestionReport({questionReport, question}: SequenceQuestionReportProps): JSX.Element {
    const renderStandardQuestions = (): JSX.Element => (
        <div className="c-questionReport__answers">
            {questionReport.answers.map((answer, index) => (
                <div className="c-questionReport__answers-item" key={index}>
                    <ReportAnswerPercentage
                        label={answer.answerText}
                        percentage={answer.percentage}
                        type={getAnswerStatus(question, answer.answerText) as QuestionAnswerType}
                        showCorrect
                        isPresenter
                    />
                </div>
            ))}
        </div>
    );

    const getOrdinalLabel = (position: number): string => {
        switch (position % 10) {
            case 1:
                return `${position}st`;
            case 2:
                return `${position}nd`;
            case 3:
                return `${position}rd`;
            default:
                return `${position}th`;
        }
    };

    const getRankKeysForAnswers = (): string[] => questionReport.answers.map((_, index) => getOrdinalLabel(index + 1));

    const getCompleteRankKeys = (rankKeys: string[]): string[] => {
        const answerRankKeys = getRankKeysForAnswers();
        return answerRankKeys.map((rankKey, index) => rankKeys[index] || rankKey);
    };

    const mapBarPlotDataToRankDistribution = (): {keys: string[]; data: RankDistributionDatum[]} => {
        const barPlotData = questionReport.sequenceQuestionData?.barPlotData || [];
        const keys = getRankKeysForAnswers();
        const data = questionReport.answers
            .map((answer) => {
                const rankCounts = barPlotData.map((rank) => Number(rank[answer.answerText] || 0));
                const noOfAnswers = rankCounts.reduce((sum, count) => sum + count, 0);
                const rankingScore = rankCounts.reduce((sum, count, index) => sum + count * (index + 1), 0);
                const rankingPercentages = keys.reduce(
                    (percentages, key, index) => ({
                        ...percentages,
                        [key]: questionReport.totalAnswers !== 0 ? Math.round((rankCounts[index] * 100) / questionReport.totalAnswers) : 0,
                    }),
                    {} as Record<string, number>
                );

                return {
                    answerText: answer.answerText,
                    rankingScore,
                    averageRanking: noOfAnswers !== 0 ? rankingScore / noOfAnswers : 0,
                    noOfAnswers,
                    ...rankingPercentages,
                };
            })
            .sort((a, b) => {
                if (a.noOfAnswers === 0 && b.noOfAnswers !== 0) return 1;
                if (b.noOfAnswers === 0 && a.noOfAnswers !== 0) return -1;
                if (a.rankingScore !== b.rankingScore) return a.rankingScore - b.rankingScore;
                return a.answerText.localeCompare(b.answerText);
            });

        return {keys, data};
    };

    const renderRankDistributionBar = (rankDistribution: RankDistributionDatum, rankDistributionKeys: string[]): JSX.Element => (
        <div className="c-sequenceReport__row" key={rankDistribution.answerText}>
            <div className="c-reportAnswerPercentage c-sequenceReport__bar">
                <div className="c-reportAnswerPercentage__background c-sequenceReport__bar-background">
                    <div aria-hidden className="c-sequenceReport__segments">
                        {rankDistributionKeys.map((rank, index) => (
                            <div
                                className="c-sequenceReport__segment"
                                key={`${rankDistribution.answerText}-${rank}`}
                                title={`${rankDistribution.answerText}: ${rankDistribution[rank] || 0}% ${rank}`}
                                style={{
                                    backgroundColor: getColor(index),
                                    width: `${Number(rankDistribution[rank] || 0)}%`,
                                }}
                            />
                        ))}
                    </div>
                    <span className="c-sequenceReport__answer-label">{rankDistribution.answerText}</span>
                </div>
            </div>
            <div className="c-sequenceReport__percentages">
                {rankDistributionKeys.map((rank) => {
                    const percentage = Number(rankDistribution[rank] || 0);
                    return (
                        <div
                            className="c-sequenceReport__percentages-item"
                            key={`${rankDistribution.answerText}-${rank}-percentage`}
                            style={{
                                width: `${percentage}%`,
                                visibility: percentage >= PERCENTAGE_VISIBILITY_LIMIT ? 'visible' : 'hidden',
                            }}
                        >
                            {percentage}%
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const renderRankDistributionLegend = (rankDistributionKeys: string[]): JSX.Element => (
        <div className="questionReportGraph__legend c-sequenceReport__legend">
            <div className="u-flex u-v-center flex-wrap c-sequenceReport__legend-items">
                {rankDistributionKeys.map((rank, index) => (
                    <div className="u-v-center u-flex" key={rank}>
                        <div
                            className="questionReportGraph__marker c-sequenceReport__legend-marker"
                            style={{
                                backgroundColor: getColor(index),
                            }}
                        />
                        <div className="questionReportGraph__answer c-sequenceReport__legend-answer">{rank}</div>
                    </div>
                ))}
            </div>
        </div>
    );

    const rankDistributionFromBarPlot = mapBarPlotDataToRankDistribution();
    const answerRankingPercentages = questionReport.answers.find((answer) => answer.rankingPercentages)?.rankingPercentages;
    const rankDistributionFromAnswers = questionReport.answers
        .filter((answer) => answer.rankingPercentages)
        .map((answer) => ({
            answerText: answer.answerText,
            rankingScore: answer.rankingScore || 0,
            averageRanking: answer.averageRanking || 0,
            noOfAnswers: answer.noOfAnswers,
            ...answer.rankingPercentages,
        }));
    const answerRankingKeys = Object.keys(answerRankingPercentages || {});

    const rankDistributionKeys = (() => {
        if (questionReport.sequenceQuestionData?.rankDistributionKeys?.length) {
            return getCompleteRankKeys(questionReport.sequenceQuestionData.rankDistributionKeys);
        }
        if (answerRankingKeys.length) {
            return getCompleteRankKeys(answerRankingKeys);
        }
        return getCompleteRankKeys(rankDistributionFromBarPlot.keys);
    })();

    const rankDistributionData = (() => {
        if (questionReport.sequenceQuestionData?.rankDistributionData?.length) {
            return questionReport.sequenceQuestionData.rankDistributionData;
        }
        if (rankDistributionFromAnswers.length) {
            return rankDistributionFromAnswers;
        }
        return rankDistributionFromBarPlot.data;
    })();

    const sortedRankDistributionData = [...rankDistributionData].sort((a, b) => {
        const aNoOfAnswers = Number(a.noOfAnswers || 0);
        const bNoOfAnswers = Number(b.noOfAnswers || 0);
        if (aNoOfAnswers === 0 && bNoOfAnswers !== 0) return 1;
        if (bNoOfAnswers === 0 && aNoOfAnswers !== 0) return -1;

        const aRankingScore = Number(a.rankingScore || Number.MAX_SAFE_INTEGER);
        const bRankingScore = Number(b.rankingScore || Number.MAX_SAFE_INTEGER);
        if (aRankingScore !== bRankingScore) return aRankingScore - bRankingScore;

        return String(a.answerText).localeCompare(String(b.answerText));
    });

    const hasAnswerRankingPercentages = questionReport.answers.some((answer: QuestionReportAnswer) => !!answer.rankingPercentages);
    const isDebatableSequenceReport = !!questionReport.isDebatable || hasAnswerRankingPercentages;
    const shouldShowRankDistribution =
        isDebatableSequenceReport && sortedRankDistributionData.length > 0 && rankDistributionKeys.length > 0;

    if (!shouldShowRankDistribution) {
        return renderStandardQuestions();
    }

    return (
        <div className="c-questionReport__answers c-sequenceReport">
            {sortedRankDistributionData.map((rankDistribution) =>
                renderRankDistributionBar(rankDistribution as RankDistributionDatum, rankDistributionKeys)
            )}
            {renderRankDistributionLegend(rankDistributionKeys)}
        </div>
    );
}

export default SequenceQuestionReport;
